import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Alert } from "@/atoms";
import type { RegisterFormFieldsInterface } from "@/components/auth";
import {
    AuthHeader,
    AuthInvalidState,
    AuthSuccessState,
    RegisterForm,
} from "@/components/auth";
import { routes, strings } from "@/constants";
import { AlertVariantEnum } from "@/enums";
import { useAcceptInvitation, useValidateInvitation } from "@/hooks";
import { AuthLayout } from "@/layouts";
import { validatePasswordHandler } from "@/utils";

const initialFields: RegisterFormFieldsInterface = {
    companyName: "",
    confirmPassword: "",
    email: "",
    name: "",
    password: "",
    phone: "",
};

export const RegisterPage = () => {
    const [token, setToken] = useState("");

    const [fields, setFields] = useState<RegisterFormFieldsInterface>(initialFields);

    const [validationError, setValidationError] = useState("");

    const [isSuccess, setIsSuccess] = useState(false);

    const [hasCheckedToken, setHasCheckedToken] = useState(false);

    const [isTokenValid, setIsTokenValid] = useState(false);

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const {
        error: serviceError,
        isLoading,
        onRun: runAcceptInvitationHandler,
    } = useAcceptInvitation();

    const {
        isLoading: isValidatingToken,
        onRun: runValidateInvitationHandler,
    } = useValidateInvitation();

    const error = validationError || serviceError || "";

    const validateFormHandler = (): string | null => {
        if (!fields.name.trim()) return strings.validation.nameRequired;

        if (!fields.companyName.trim()) return strings.validation.companyNameRequired;

        if (!fields.phone.trim()) return strings.validation.phoneRequired;

        const passwordValidation = validatePasswordHandler(fields.password);

        if (!passwordValidation.isValid) return passwordValidation.error || strings.errors.checkInput;

        if (fields.password !== fields.confirmPassword) return strings.register.passwordMismatch;

        return null;
    };

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setValidationError("");

        const formError = validateFormHandler();

        if (formError) {
            setValidationError(formError);

            return;
        }

        try {
            await runAcceptInvitationHandler(
                token,
                {
                    companyName: fields.companyName,
                    email: fields.email,
                    name: fields.name,
                    password: fields.password,
                    passwordConfirmation: fields.confirmPassword,
                    phone: fields.phone,
                },
            );

            setIsSuccess(true);
        } catch {
            // Error captured by hook
        }
    };

    const backToLoginHandler = () => navigate(routes.login);

    const requestNewInvitationHandler = () => {
        const subject = encodeURIComponent(strings.register.requestNewInvitationSubject);

        const body = encodeURIComponent(strings.register.requestNewInvitationBody);

        window.location.href = `mailto:${strings.register.requestNewInvitationEmail}?subject=${subject}&body=${body}`;
    };

    const changeFieldHandler = (key: keyof RegisterFormFieldsInterface, value: string) => {
        setFields((prev) => ({
            ...prev,
            [key]: value,
        }));

        setValidationError("");
    };

    useEffect(
        () => {
            const tokenParam = searchParams.get(strings.queryParams.token);

            if (!tokenParam) {
                setHasCheckedToken(true);

                setIsTokenValid(false);

                return;
            }

            setToken(tokenParam);

            (async () => {
                const result = await runValidateInvitationHandler(tokenParam);

                setIsTokenValid(result.isValid);

                if (result.isValid && result.email) {
                    setFields((prev) => ({
                        ...prev,
                        email: result.email,
                    }));
                }

                setHasCheckedToken(true);
            })();
        },
        [searchParams, runValidateInvitationHandler],
    );

    if (!hasCheckedToken || isValidatingToken) {
        return (
            <AuthLayout
                description={strings.register.sidebarDescription}
                title={strings.register.sidebarTitle}
            >
                <div
                    className="
                        flex
                        h-full
                        min-h-[40vh]
                        items-center
                        justify-center
                    "
                >
                    <span className="text-base text-text-secondary">{strings.common.loading}</span>
                </div>
            </AuthLayout>
        );
    }

    if (isSuccess) {
        return (
            <AuthLayout
                description={strings.register.sidebarDescription}
                title={strings.register.sidebarTitle}
            >
                <AuthSuccessState
                    actionLabel={strings.register.goToLogin}
                    message={strings.register.successMessage}
                    title={strings.register.title}
                    isFullWidth
                    onAction={backToLoginHandler}
                />
            </AuthLayout>
        );
    }

    if (!token || !isTokenValid) {
        return (
            <AuthLayout
                description={strings.register.sidebarDescription}
                title={strings.register.sidebarTitle}
            >
                <AuthInvalidState
                    actionLabel={strings.register.backToLogin}
                    message={strings.register.invalidToken}
                    primaryActionLabel={strings.register.requestNewInvitationLabel}
                    title={strings.register.title}
                    onAction={backToLoginHandler}
                    onPrimaryAction={requestNewInvitationHandler}
                />
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            description={strings.register.sidebarDescription}
            title={strings.register.sidebarTitle}
        >
            <div>
                <AuthHeader
                    description={strings.register.description}
                    title={strings.register.title}
                />
                {error && (
                    <Alert
                        className="mb-6"
                        variant={AlertVariantEnum.ERROR}
                        onClose={() => setValidationError("")}
                    >
                        {error}
                    </Alert>
                )}
                <RegisterForm
                    error={error}
                    fields={fields}
                    isLoading={isLoading}
                    onBackToLogin={backToLoginHandler}
                    onChangeField={changeFieldHandler}
                    onSubmit={submitHandler}
                />
            </div>
        </AuthLayout>
    );
};
